import { Fragment, useState, useEffect, useCallback } from "react";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import makeStyles from "styles/muiStylesCompat/makeStyles";
import Dialog from "@mui/material/Dialog";
import MuiDialogTitle from "@mui/material/DialogTitle";
import MuiDialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";

import {
  httpGetPrice,
  httpCreatePrice,
  httpUpdatePrice,
  httpDeletePrice,
} from "~/services/http/prices";

import MyTextField from "components/Inputs/MyTextField";
import ButtonSuccess from "~/components/Buttons/ButtonSuccess";
import ButtonDelete from "~/components/Buttons/ButtonDelete";
import ButtonDefault from "~/components/Buttons/ButtonDefault";
import SpinnerCentered from "~/components/Loader/SpinnerCentered";

const defaultValues = {
  id: -1,
  name: "",
  opening_price: "0",
  price_per_unit: "0",
  special_opening_price: "0",
  special_price_per_unit: "0",
};

const DialogTitle = (props) => {
  const { children, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography sx={{ m: 0, p: 2 }} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(theme) => ({
            position: "absolute",
            right: theme.spacing(1),
            top: theme.spacing(1),
            color: theme.palette.grey[500],
          })}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
};

const DialogContent = (props) => (
  <MuiDialogContent
    sx={(theme) => ({ padding: theme.spacing(2) })}
    {...props}
  />
);

const useStyles = makeStyles((_theme) => ({
  paperRoot: {
    borderRadius: "25px",
  },
}));

// helper for yup transform function
function emptyStringToNull(value, originalValue) {
  if (typeof originalValue === "string" && originalValue === "") {
    return null;
  }
  return value;
}

function importPriceData(data) {
  return {
    ...data,
    opening_price: data?.opening_price / 100.0,
    price_per_unit: data?.price_per_unit / 100.0,
    special_opening_price: data?.special_opening_price / 100.0,
    special_price_per_unit: data?.special_price_per_unit / 100.0,
  };
}

function exportPriceData(data) {
  return {
    ...data,
    opening_price: (data?.opening_price * 100.0).toFixed(0),
    price_per_unit: (data?.price_per_unit * 100.0).toFixed(0),
    special_opening_price: (data?.special_opening_price * 100.0).toFixed(0),
    special_price_per_unit: (data?.special_price_per_unit * 100.0).toFixed(0),
  };
}

export default function CustomizedDialogs(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { open, handleClose, id, getMyCompany } = props;

  const [values, setValues] = useState(defaultValues);
  const [errors, setErrors] = useState({ inValid: true });
  const [loading, setLoading] = useState(false);

  const validationSchema = yup.object().shape({
    special_price_per_unit: yup
      .number()
      .required(t("Pakollinen kenttä"))
      .transform(emptyStringToNull)
      .nullable(),
    special_opening_price: yup
      .number()
      .required(t("Pakollinen kenttä"))
      .transform(emptyStringToNull)
      .nullable(),
    price_per_unit: yup
      .number()
      .required(t("Pakollinen kenttä"))
      .transform(emptyStringToNull)
      .nullable(),
    opening_price: yup
      .number()
      .required(t("Pakollinen kenttä"))
      .transform(emptyStringToNull)
      .nullable(),
    name: yup.string().required(t("Pakollinen kenttä")),
  });

  const updateValues = (updated_values) => {
    if (updated_values == null) {
      return;
    }

    setValues((prev) => ({
      ...prev,
      ...updated_values,
    }));
  };

  const handleFormChange = ({ target }) => {
    updateValues({ [target.name]: target.value });
  };

  const validateForm = () => {
    validationSchema
      .validate(values, { abortEarly: true })
      .then(() => {
        // GOOD
        setErrors({});
      })
      .catch((err) => {
        // INVALID
        setErrors({
          [err.path]: t(err?.message),
          inValid: true,
        });
      });
  };

  const getPrice = useCallback(() => {
    setLoading(true);

    httpGetPrice(id).then(
      (response) => {
        setValues(importPriceData(response));
        setErrors({});
        setLoading(false);
      },
      (_error) => {
        setLoading(false);
      },
    );
  }, [id]);

  useEffect(() => {
    if (id > -1) {
      getPrice();
    } else {
      setValues(defaultValues);
      setErrors({ inValid: true });
    }
  }, [id, getPrice]);

  const savePrice = () => {
    setLoading(true);

    let data = exportPriceData(values);

    if (values?.id != null && values?.id > -1) // Update
    {
      httpUpdatePrice(id, data).then(
        (response) => {
          setValues(importPriceData(response));
          setLoading(false);
          getMyCompany();
          handleClose();
        },
        (_error) => {
          setLoading(false);
        },
      );
    } else // create
    {
      httpCreatePrice(data).then(
        (response) => {
          setValues(importPriceData(response));
          setLoading(false);
          getMyCompany();
          handleClose();
        },
        (_error) => {
          setLoading(false);
        },
      );
    }
  };

  const deletePrice = () => {
    setLoading(true);

    if (values?.id != null && values?.id > -1) // Delete
    {
      httpDeletePrice(id).then(
        (_response) => {
          setLoading(false);
          getMyCompany();
          handleClose();
        },
        (_error) => {
          setLoading(false);
        },
      );
    }
  };

  return (
    <div>
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        classes={{ paper: classes.paperRoot }}
      >
        <DialogTitle id="customized-dialog-title" onClose={handleClose}>
          {values?.id > -1 ? (
            <Fragment>{t("Muokkaa hinnastoa")}</Fragment>
          ) : (
            <Fragment>{t("Lisää uusi hinnasto")}</Fragment>
          )}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <SpinnerCentered />
          ) : (
            <Fragment>
              <MyTextField
                autoFocus
                disabled={values?.id === 0}
                customLabel={t("Hinnaston nimi")}
                required
                id="price_dialog_name"
                name="name"
                value={values.name || ""}
                onChange={handleFormChange}
                onBlur={validateForm}
                error={errors?.name}
                helperText={errors?.name}
              />

              <Typography variant="subtitle2">
                {t("Perusmaksut (ma-la klo 06-18)")}
              </Typography>

              <MyTextField
                customLabel={t("Aloitusmaksu")}
                required
                id="price_dialog_opening_price"
                name="opening_price"
                value={values.opening_price || ""}
                onChange={handleFormChange}
                onBlur={validateForm}
                error={errors?.opening_price}
                helperText={errors?.opening_price}
                type="number"
              />

              <MyTextField
                customLabel={t("Hinta/km")}
                required
                id="price_dialog_price_per_unit"
                name="price_per_unit"
                value={values.price_per_unit || ""}
                onChange={handleFormChange}
                onBlur={validateForm}
                error={errors?.price_per_unit}
                helperText={errors?.price_per_unit}
                type="number"
              />

              <Typography variant="subtitle2">
                {t("Muut ajat (pyhät ja illat)")}
              </Typography>

              <MyTextField
                customLabel={t("Aloitusmaksu")}
                required
                id="price_dialog_special_opening_price"
                name="special_opening_price"
                value={values.special_opening_price || ""}
                onChange={handleFormChange}
                onBlur={validateForm}
                error={errors?.special_opening_price}
                helperText={errors?.special_opening_price}
                type="number"
              />

              <MyTextField
                customLabel={t("Hinta/km")}
                required
                id="price_dialog_special_price_per_unit"
                name="special_price_per_unit"
                value={values.special_price_per_unit || ""}
                onChange={handleFormChange}
                onBlur={validateForm}
                error={errors?.special_price_per_unit}
                helperText={errors?.special_price_per_unit}
                type="number"
              />
            </Fragment>
          )}
        </DialogContent>
        <Grid
          container
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Grid size={12}>
            <Grid
              container
              direction="row"
              sx={{ justifyContent: "space-between", alignItems: "center" }}
            >
              <ButtonDefault onClick={handleClose}>
                {t("Peruuta")}
              </ButtonDefault>
              {values?.id > -1 ? (
                <ButtonDelete onClick={deletePrice} disabled={values?.id === 0}>
                  {t("Poista")}
                </ButtonDelete>
              ) : null}
            </Grid>
          </Grid>
          <Grid size={12}>
            <ButtonSuccess
              disabled={errors.inValid || loading}
              onClick={savePrice}
            >
              {t("Tallenna")}
            </ButtonSuccess>
          </Grid>
        </Grid>
      </Dialog>
    </div>
  );
}
